import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";

import { Card, CardContent, Grid, Paper, Typography } from "@material-ui/core";

import previewModalStyles from "components/ServiceAndSlots/PreviewModal/PreviewModalStyles";

import { ALERT_TYPES, SERVICE_TYPES, STATUS_OK, DELIVERY_TYPES, ITEMS_PER_BAG_REG_EXP, FEE_REG_EXP_VALUE } from "../../config/constants";
import { fetchServiceInActiveStatus, selectAllCountries, serviceSelectPropositionRecord } from "../../config/redux/configurationsSlice";
import { ReactComponent as CloseButton } from "../assets/svg/closeButton.svg";

import ModalContainer from "base/ModalContainer";
import ConfirmModal from "../common/ConfirmModal/ConfirmModal";
import CustomAlert from "../common/CustomAlert/CustomAlert";
import Loader from "../common/Loader/Loader";

import { countrySettingsStyles } from "./CountrySettingsStyles";
import { ClickCollectInitConfig, COUNTRY_SETTING_TEXT_CONSTANT, countrySettingsDetailsPayloadNew, DEFAULT_DAY_BASED_OBJ, DEFAULT_MINUTE_BASED_OBJ, DEFAULT_SLOT_BASED_OBJ, ExpressInitConfig, FBCInitialConfig, getDateFromPicker, getHolidaysListObject } from "./CountrySettingsUtils";
import { PosTabInfo } from "./CountrySettingView";
import { CountryChangesConfirmationModal } from "./modal/CountryChangesConfirmationModal";
import { createCountrySettings, fetchActivePOSAndServiceTypesCount, fetchCountrySettings, updateCountrySettings } from "./redux/countrySettingsSlice";
import { isArrayValid, isEmptyOrNull, isNonEmptyObject } from "../../utils/DataUtils";

import CountrySettingsFooter from "./common/CountrySettingsFooter";
import RenderStandardServiceTypeDetails from "./common/RenderStandardServiceTypeDetails";
import RenderExpressServiceTypeDetails from "./common/RenderExpressServiceTypeDetails";
import RenderClickAndCollectServiceTypeDetails from "./common/RenderClickAndCollectServiceTypeDetails";
import RenderDefaultTypeDetails from "./common/RenderDefaultTypeDetails";
import RenderFBCTypeDetails from "./common/RenderFBCTypeDetails";

const CountrySettings = () => {
  const { countryName } = useParams();

  const classes = countrySettingsStyles();
  const dispatch = useDispatch();

  const {
    countrySettings: {
      posCount,
      fetchingPosCount,
      loading,
      countryDetails: { data: savedCountryDetails },
    },
  } = useSelector((state) => state);
  const servicePropositionList = useSelector(serviceSelectPropositionRecord);
  const allCountries = useSelector(selectAllCountries);

  const { countryId } = allCountries.length ? allCountries.find((country) => country.name === countryName) : {};

  const expressType = "";
  const fbcDetails = FBCInitialConfig;

  const [configs, setConfigs] = useState({});
  const [selectedServiceType, setSelectedServiceType] = useState("");
  const [selectedDeliveryType, setSelectedDeliveryType] = useState("");
  const [expressDetails, setExpressDetails] = useState(ExpressInitConfig);
  const [clickCollectDetails, setClickCollectDetails] = useState(ClickCollectInitConfig);
  const [showApplyChangesModal, setShowApplyChangesModal] = useState(false);
  const [showExpressTypeChangeConfirmModal, setShowExpressTypeChangeConfirmModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState({ show: false, duplicates: [], oldDates: false });
  const [defaultDateState, setDefaultDateState] = useState({ dateFrom: "", dateTo: "", dateLabel: "" });

  // Sort combinedResponse based on rank
  const sortedCombinedResponse = useMemo(() => {
    const serviceRecord = servicePropositionList
      .reduce((acc, service) => {
        if (service.deliveryType) {
          const count = posCount[service.name] || 0;
          acc.push({ ...service, count });
        }
        return acc;
      }, [])
      .sort((a, b) => a.rank - b.rank);

    if (isArrayValid(serviceRecord)) {
      const { deliveryType, displayName } = serviceRecord[0];
      setSelectedServiceType(displayName?.toUpperCase() ?? "");
      setSelectedDeliveryType(deliveryType ?? "");
    }
    return serviceRecord;
  }, [servicePropositionList, posCount]);

  const handleServiceTypeDetails = (service) => {
    const { deliveryType, displayName } = service;
    setSelectedServiceType(displayName.toUpperCase());
    setSelectedDeliveryType(deliveryType);
  };

  const handleSlotBasedDetailChanges = (serviceType, event) => {
    if (!isEmptyOrNull(event?.target)) {
      const { name, value } = event.target;
      let newValue = value === "true" || value === true;
      setConfigs((prevConfigs) => {
        const updatedConfig = { ...prevConfigs[serviceType], [name]: newValue };
        if (name === "standardAdvanceSlotDays") {
          updatedConfig.editSlotDays = value;
        }
        if (name === "standardNextDaySlotVisibility") {
          updatedConfig.nextDaySlotVisibility = newValue;
        }
        return { ...prevConfigs, [serviceType]: updatedConfig };
      });
    }
  };

  const handleMinuteBasedChanges = (serviceType, event) => {
    if (!isEmptyOrNull(event?.target)) {
      const { name, value } = event.target;
      let newValue = value === "true";
      setConfigs((prevConfigs) => {
        const updatedConfig = { ...prevConfigs[serviceType], [name]: newValue };
        if (name === "expressNextDaySlotVisibility") {
          updatedConfig.nextDaySlotVisibility = newValue;
        } else if (name === "expressType") {
          updatedConfig.expressType = value;
        } else if (name === "expressAdvanceSlotDays") {
          updatedConfig.editSlotDays = value;
        }
        return { ...prevConfigs, [serviceType]: updatedConfig };
      });
    }
  };

  const handleExpressTypeChangeApprove = () => {
    setExpressDetails({ ...expressDetails, expressType });
    setShowExpressTypeChangeConfirmModal(false);
  };

  const handleClickAndCollectDetailsChange = (event) => {
    if (!isEmptyOrNull(event?.target)) {
      const { name, value } = event.target;
      let newValue = value === "true";
      setClickCollectDetails({ ...clickCollectDetails, [name]: newValue });
      switch (name) {
        case "cncNextDaySlotVisibility":
          setClickCollectDetails({ ...clickCollectDetails, nextDaySlotVisibility: newValue });
          break;
        case "cncAdvanceSlotDays":
          setClickCollectDetails({ ...clickCollectDetails, editSlotDays: value });
          break;
        default:
          return "";
      }
    }
  };

  const handleApplyChangesModalClose = () => {
    setShowApplyChangesModal(false);
  };

  const validateBagCharges = (config) => {
    if (!config.bagChargeEnabled) {
      return true;
    }

    const chargesSource = config.editBagCharge ? config.newPlasticBagCharges : config;
    const serviceTypeName = config?.serviceType ? `for ${config.serviceType}.` : ".";
    if (isEmptyOrNull(chargesSource?.itemsPerBag)) {
      CustomAlert(ALERT_TYPES.ERROR, `${COUNTRY_SETTING_TEXT_CONSTANT.INVALID_INPUT_ITEMS_PER_BAG} ${serviceTypeName}`);
      return false;
    }

    if (isEmptyOrNull(chargesSource?.bagCharges)) {
      CustomAlert(ALERT_TYPES.ERROR, `${COUNTRY_SETTING_TEXT_CONSTANT.INVALID_INPUT_BAG_CHARGES} ${serviceTypeName}`);
      return false;
    }

    return true;
  };

  const handleApplyChangesClick = () => {
    for (const key of Object.keys(configs)) {
      const config = configs[key];
      if (!validateBagCharges(config)) {
        return;
      }
    }
    setShowApplyChangesModal(true);
  };

  const handleSuccessPut = () => {
    setShowApplyChangesModal(false);
    dispatch(fetchCountrySettings({ countryId }));
  };

  const handleSubmit = async () => {
    let requestPayload = { countryId, countryData: countrySettingsDetailsPayloadNew(countryName, configs) };
    if (!(savedCountryDetails && Object.keys(savedCountryDetails).length)) {
      const { payload } = await dispatch(createCountrySettings(requestPayload)); // NOSONAR
      if (payload?.status === STATUS_OK) {
        CustomAlert(ALERT_TYPES.SUCCESS, COUNTRY_SETTING_TEXT_CONSTANT.SUCCESS);
        dispatch(fetchServiceInActiveStatus(countryId));
        handleSuccessPut();
      } else if (payload?.message) {
        CustomAlert(ALERT_TYPES.ERROR, COUNTRY_SETTING_TEXT_CONSTANT.ERROR);
      }
    } else {
      const { payload } = await dispatch(updateCountrySettings(requestPayload));  // NOSONAR
      if (payload?.status === STATUS_OK) {
        CustomAlert(ALERT_TYPES.SUCCESS, COUNTRY_SETTING_TEXT_CONSTANT.UPDATED);
        dispatch(fetchServiceInActiveStatus(countryId));
        handleSuccessPut();
      } else if (payload?.message) {
        CustomAlert(ALERT_TYPES.ERROR, COUNTRY_SETTING_TEXT_CONSTANT.ERROR_UPDATING);
      }
    }
  };

  const handleDateChangeEvent = (event, picker) => {
    if (!isEmptyOrNull(event?.target)) {
      if (event.target.name === "date") {
        const dateStart = getDateFromPicker(picker.startDate);
        const dateEnd = getDateFromPicker(picker.endDate);
        let label = "";
        label = dateStart === dateEnd ? dateStart : dateStart + " - " + dateEnd;
        setDefaultDateState({ dateFrom: dateStart, dateTo: dateEnd, dateLabel: label });
      }
    }
  };

  const handleCalendarHoliday = async (event, type, label, serviceType) => {
    if (!label || label.trim().length === 0) {
      return;
    }
    const dateFrom = new Date(defaultDateState.dateFrom);
    const dateTo = new Date(defaultDateState.dateTo);
    const currentDate = new Date();

    if (type === COUNTRY_SETTING_TEXT_CONSTANT.ADD) {
      if (dateFrom <= currentDate || dateTo <= currentDate) {
        setShowInfoModal({ show: true, duplicates: [], oldDates: true });
        return;
      }
      let list = await getHolidaysListObject(defaultDateState);
      let arr = list.map((item) => item.objectDate);
      let allDates = [...arr, ...configs[serviceType].holidays.map((holiday) => holiday.offDate || holiday.offDay)];
      let duplicate = allDates.filter((e, i, a) => a.indexOf(e) !== i);
      if (duplicate && duplicate.length > 0) {
        setShowInfoModal({ show: true, duplicates: duplicate, oldDates: false });
        return;
      }

      setConfigs((prevConfigs) => {
        const updatedHolidays = [...prevConfigs[serviceType].holidays, defaultDateState];
        return { ...prevConfigs, [serviceType]: { ...prevConfigs[serviceType], holidays: updatedHolidays } };
      });
      setDefaultDateState({ dateFrom: "", dateTo: "", dateLabel: "" });
    }

    if (type === COUNTRY_SETTING_TEXT_CONSTANT.REMOVE) {
      setConfigs((prevConfigs) => {
        const updatedHolidays = prevConfigs[serviceType].holidays.filter((item) => {
          return item.dateLabel !== label && item.offDate !== label;
        });
        return { ...prevConfigs, [serviceType]: { ...prevConfigs[serviceType], holidays: updatedHolidays } };
      });
    }
  };

  const handleCancel = (event, picker) => {
    event.preventDefault();
    picker.element.val("");
    setDefaultDateState({ dateFrom: "", dateTo: "", dateLabel: "" });
  };

  const handleBagChargeChange = (event, serviceType) => {
    if (!isEmptyOrNull(event?.target)) {
      const { value } = event.target;
      let newValue = value === "true" || value === true;
      if (serviceType) {
        setConfigs((prevConfigs) => ({
          ...prevConfigs,
          [serviceType]: {
            ...prevConfigs[serviceType],
            bagChargeEnabled: newValue
          }
        }));
      }
    }
  };

  const handleServiceTypeActiveChange = (event, serviceTypeName) => {
    if (!isEmptyOrNull(serviceTypeName)) {
      setConfigs((prevConfigs) => ({
        ...prevConfigs,
        [serviceTypeName]: {
          ...prevConfigs[serviceTypeName],
          serviceActive: !prevConfigs[serviceTypeName].serviceActive
        }
      }));
    }
  };

  const getDefaultConfig = (deliveryType, serviceTypeID) => {
    switch (deliveryType) {
      case DELIVERY_TYPES.DAY_BASED:
        return { ...DEFAULT_DAY_BASED_OBJ, serviceTypeID };
      case DELIVERY_TYPES.MINUTE_BASED:
        return { ...DEFAULT_MINUTE_BASED_OBJ, serviceTypeID };
      case DELIVERY_TYPES.SLOT_BASED:
        return { ...DEFAULT_SLOT_BASED_OBJ, serviceTypeID };
      default:
        return { serviceTypeID };
    }
  };

  const getWeeklyHolidays = (holidays) => {
    return holidays.filter(holiday => holiday.offDay).map(holiday => holiday.offDay);
  };

  const updateCountryDetailsInfo = () => {
    const { serviceTypes: savedServiceTypes } = savedCountryDetails;
    const initialConfigs = {};

    if (!isArrayValid(savedServiceTypes)) {
      setConfigs(initialConfigs);
      return;
    }

    sortedCombinedResponse.forEach((item) => {
      const defaultConfig = getDefaultConfig(item.deliveryType, item.id);
      const savedConfig = savedServiceTypes.find((service) => service.serviceTypeID === item.id);

      const mergedConfig = savedConfig ? { ...defaultConfig, ...savedConfig } : defaultConfig;

      if (item.deliveryType === DELIVERY_TYPES.DAY_BASED && savedConfig?.holidays) {
        mergedConfig.weeklyHolidays = getWeeklyHolidays(savedConfig.holidays);
      }

      initialConfigs[item.displayName.toUpperCase()] = mergedConfig;
    });
    setConfigs(initialConfigs);
  };

  const handleDetailsChange = (event, serviceType) => {
    if (event !== undefined) {
      setConfigs((prevConfigs) => ({ ...prevConfigs, [serviceType]: { ...prevConfigs[serviceType], weeklyHolidays: [...event] } }));
    }
  };

  const handleEditBagCharges = (serviceType) => {
    if (!isEmptyOrNull(serviceType)) {
      setConfigs((prevConfigs) => ({
        ...prevConfigs,
        [serviceType]: {
          ...prevConfigs[serviceType],
          editBagCharge: !prevConfigs[serviceType].editBagCharge,
          newPlasticBagCharges: {
            itemsPerBag: prevConfigs[serviceType].itemsPerBag,
            bagCharges: prevConfigs[serviceType].bagCharges
          }
        }
      }));
    }
  };
  const handleAdvanceDaysClick = (serviceType) => {
    if (!isEmptyOrNull(serviceType)) {
      setConfigs((prevConfigs) => ({
        ...prevConfigs,
        [serviceType]: {
          ...prevConfigs[serviceType],
          showAdvanceSlotDaysInput: true,
        },
      }));
    }
  };
  const handlePlasticBagChange = (event, serviceType) => {
    if (isEmptyOrNull(event?.target)) {
      return;
    }

    const { name, value } = event.target;
    switch (name) {
      case "itemsPerBag":
        if (!isEmptyOrNull(value) && !ITEMS_PER_BAG_REG_EXP.test(value)) {
          console.error(COUNTRY_SETTING_TEXT_CONSTANT.INVALID_INPUT_ITEMS_PER_BAG);
          return;
        }
        break;
      case "bagCharges":
        if (!isEmptyOrNull(value) && !FEE_REG_EXP_VALUE.test(value)) {
          console.error(COUNTRY_SETTING_TEXT_CONSTANT.INVALID_INPUT_BAG_CHARGES);
          return;
        }
        break;
      default:
        break;
    }
    setConfigs((prevConfigs) => ({
      ...prevConfigs,
      [serviceType]: {
        ...prevConfigs[serviceType],
        newPlasticBagCharges: {
          ...prevConfigs[serviceType].newPlasticBagCharges,
          [name]: value,
        }
      },
    }));
  };

  const viewServiceContent = () => {
    return isArrayValid(sortedCombinedResponse) && !isEmptyOrNull(selectedDeliveryType) && isNonEmptyObject(configs);
  }

  const renderNoServiceTypeSelected = () => {
    return (
      <Card className={classes.serviceTypeCard}>
        <CardContent>
          <Grid className={classes.noServiceItem}>
            <Typography className={classes.noServiceSpanStyle}>
              {`${COUNTRY_SETTING_TEXT_CONSTANT.NO_SERVICE_TYPE_SELECTED}`}
            </Typography>
          </Grid>
        </CardContent>
      </Card>
    );
  }

  const renderDefaultTypeDetails = (commonProps) => {
    if (isEmptyOrNull(selectedServiceType)) {
      return renderNoServiceTypeSelected();
    }

    if (selectedServiceType === SERVICE_TYPES.CLICK_N_COLLECT) {
      return (
        <RenderClickAndCollectServiceTypeDetails
          {...commonProps}
          CountryMappingTypeView
          posCount={posCount}
          clickCollectDetails={configs[selectedServiceType]}
          handleClickAndCollectDetailsChange={handleClickAndCollectDetailsChange}
        />
      );
    } else if (selectedServiceType === SERVICE_TYPES.FBC) {
      return (
        <RenderFBCTypeDetails
          classes={classes}
          fbcDetails={fbcDetails}
          handleServiceTypeActiveChange={handleServiceTypeActiveChange}
        />
      );
    } else {
      return renderNoServiceTypeSelected();
    }
  }

  const renderServiceContent = () => {
    const commonProps = {
      classes,
      handleBagChargeChange: (event) => handleBagChargeChange(event, selectedServiceType),
      handleServiceTypeActiveChange: (event) => handleServiceTypeActiveChange(event, selectedServiceType),
      handleEditBagCharges: () => handleEditBagCharges(selectedServiceType),
      handlePlasticBagChange: (event) => handlePlasticBagChange(event, selectedServiceType),
      handleAdvanceDaysClick: () => handleAdvanceDaysClick(selectedServiceType),
    };

    switch (selectedDeliveryType) {
      case DELIVERY_TYPES.SLOT_BASED:
        return (
          <RenderStandardServiceTypeDetails
            {...commonProps}
            standardDetails={configs[selectedServiceType]}
            handleStandardDetailsChange={(event) => handleSlotBasedDetailChanges(selectedServiceType, event)}
            serviceTypeName={selectedServiceType}
          />
        );

      case DELIVERY_TYPES.MINUTE_BASED:
        return (
          <RenderExpressServiceTypeDetails
            {...commonProps}
            expressDetails={configs[selectedServiceType]}
            handleExpressDetailsChange={(event) => handleMinuteBasedChanges(selectedServiceType, event)}
            serviceTypeName={selectedServiceType}
          />
        );

      case DELIVERY_TYPES.DAY_BASED:
        return (
          <RenderDefaultTypeDetails
            {...commonProps}
            defaultDetails={configs[selectedServiceType]}
            configs={configs}
            weeklyHolidays={configs[selectedServiceType]?.weeklyHolidays || []}
            setWeeklyHolidays={(event) => handleDetailsChange(event, selectedServiceType)}
            defaultDateState={defaultDateState}
            handleDateChangeEvent={handleDateChangeEvent}
            handleCancel={handleCancel}
            handleCalendarHoliday={(event, type, label) => handleCalendarHoliday(event, type, label, selectedServiceType)}
            calendarHoliday={configs[selectedServiceType]?.holidays || []}
            serviceTypeName={selectedServiceType}
          />
        );

      default:
        return renderDefaultTypeDetails(commonProps);
    }
  };

  useEffect(() => {
    if (isNonEmptyObject(savedCountryDetails)) {
      updateCountryDetailsInfo();
    }
  }, [savedCountryDetails]);

  useEffect(() => {
    if (isArrayValid(allCountries)) {
      dispatch(fetchCountrySettings({ countryId }));
    }
  }, [dispatch, countryId, allCountries.length]);

  useEffect(() => {
    dispatch(fetchActivePOSAndServiceTypesCount({ country: countryName }));
  }, [dispatch, countryName]);

  return (
    <div className={classes.countrySection}>
      <Typography className={classes.title}>{`${COUNTRY_SETTING_TEXT_CONSTANT.COUNTRY_SETTING_LABEL} ${countryName}`}</Typography>

      {fetchingPosCount ? (
        <Loader />
      ) : (
        <>
          <Paper className={classes.posDetailsPaperStyle}>
            <Grid container spacing={0}>
              {sortedCombinedResponse?.map((element, index) => (
                <Grid
                  item
                  key={`${index}-${element?.name}`}
                  onClick={() => handleServiceTypeDetails(element)}
                  className={`${classes.serviceTab} ${selectedServiceType === element.displayName?.toUpperCase() ? classes.serviceTypeDetailsSectionActive : ""}`}
                  sx={{ flex: 1 }}
                >
                  <PosTabInfo element={element} classes={classes} />
                </Grid>
              ))}
            </Grid>
          </Paper>
          {viewServiceContent() ? renderServiceContent() : renderNoServiceTypeSelected()}
          <CountrySettingsFooter classes={classes} handleApplyChangesClick={handleApplyChangesClick} />
        </>
      )}

      {/* Modals */}
      {showInfoModal.show && (
        <InfoModal
          show={showInfoModal.show}
          oldDates={showInfoModal.oldDates}
          onClose={() => setShowInfoModal({ show: false, duplicates: [], oldDates: false })}
          holidays={showInfoModal.duplicates}
        />
      )}
      {showApplyChangesModal && (
        <CountryChangesConfirmationModal
          open={showApplyChangesModal}
          config={configs}
          onClose={handleApplyChangesModalClose}
          classes={classes}
          loading={loading}
          handleSubmit={handleSubmit}
        />
      )}
      {showExpressTypeChangeConfirmModal && (
        <ConfirmModal
          show={showExpressTypeChangeConfirmModal}
          title={`${COUNTRY_SETTING_TEXT_CONSTANT.CONFIRM_EXPRESS_TYPE_CHANGE}`}
          description={`${COUNTRY_SETTING_TEXT_CONSTANT.SLOTS_NEEDS_REVALIDATION}`}
          confirmButtonText={`${COUNTRY_SETTING_TEXT_CONSTANT.APPROVE}`}
          onClose={() => setShowExpressTypeChangeConfirmModal(false)}
          onProceed={handleExpressTypeChangeApprove}
        />
      )}
    </div>
  );
};

export default CountrySettings;

const InfoModal = ({ show, onClose, holidays, oldDates }) => {
  const classes = previewModalStyles({});

  return (
    <div className={classes.modalContainer}>
      <ModalContainer className={classes.modal} open={show} onClose={onClose} disableBackdropClick>
        <div className={`${classes.modalContent} ${classes.modalContentWindow}`}>
          <div className={classes.closeButton}>
            <CloseButton className={classes.closeButtonSVG} onClick={onClose} />
          </div>

          <div className={classes.modalHeader}>
            <div className={classes.modalTitle}>{oldDates ? COUNTRY_SETTING_TEXT_CONSTANT.OLDER_DATES : COUNTRY_SETTING_TEXT_CONSTANT.DUPLICATE_HOLIDAYS}</div>
          </div>

          <div className={classes.modalBody}>
            {oldDates ? (
              <Typography>{`${COUNTRY_SETTING_TEXT_CONSTANT.OLDER_DATE_SELECTED_ERROR}`}</Typography>
            ) : (
              <>
                <Typography className={classes.applyInfoModalBodyContent}>{`${COUNTRY_SETTING_TEXT_CONSTANT.DATES_ALREADY_SELECTED_ERROR}`}</Typography>
                <div style={{ marginTop: "10px" }}>{holidays?.map((info) => info).join(", ")}</div>
              </>
            )}
          </div>
        </div>
      </ModalContainer>
    </div>
  );
};

InfoModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  holidays: PropTypes.array,
  oldDates: PropTypes.bool,
};
